<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    width="800px"
    class="instagram-instantwin-prize-edit-dialog"
  >
    <div slot="title">{{ isEditMode ? '応募グループの編集' : '応募グループの新規作成' }}</div>

    <div v-loading="loading" class="dialog-content">
      <!-- 基本設定 -->
      <div class="caption mb-x2">{{ '基本設定' }}</div>
      <div class="setting-content">
        <Panel>
          <!-- 応募グループ名 -->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="応募グループ名" required></FormItemLabel>
              <HelpTooltip>{{ '応募グループの名称を入力してください。' }}</HelpTooltip>
            </div>
            <div class="input">
              <el-input
                v-model="formData.name"
                :placeholder="'応募グループ名を入力してください'"
                maxlength="256"
                :disabled="loading"
                :class="{ 'is-error': validationErrors.name }"
              />
            </div>
          </div>
        </Panel>
        <Panel>
          <!-- 応募キーワード -->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="応募キーワード"></FormItemLabel>
              <HelpTooltip>{{ 'この応募グループへの応募を判定するキーワードを入力してください。' }}</HelpTooltip>
            </div>
            <div class="help-text">{{ '全て含む' }}</div>
            <Tags
              :value="formData.entryKeyword"
              :editable="!loading"
              :max-length="120"
              type="keyword"
              sns-type="instagram"
              @input="onChangeEntryKeywords"
            />
          </div>
        </Panel>
        <Panel>
          <!-- 1日の切り替え時間 -->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="1日の切り替え時間"></FormItemLabel>
              <HelpTooltip>{{ '1日の当選数をリセットする時間を設定できます。' }}</HelpTooltip>
            </div>
            <el-time-select
              class="daily-change-time-picker"
              v-model="formData.dailyWinnerChangeTime"
              :picker-options="{
                start: '00:00',
                step: '01:00',
                end: '23:00',
                format: 'HH:mm'
              }"
              :placeholder="'時間を選択'"
              prefix-icon="mdi mdi-clock"
              :editable="false"
              :disabled="loading"
            />
          </div>
        </Panel>
      </div>

      <!-- 当選条件設定 -->
      <div class="caption mb-x2">{{ '当選条件設定' }}</div>
      <div class="setting-content">
        <Panel>
          <!-- 当選数 -->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="当選数" required></FormItemLabel>
              <HelpTooltip>{{ '応募グループの当選数を指定してください。' }}</HelpTooltip>
            </div>
            <el-input-number
              v-model="formData.maxWinnerCount"
              :min="0"
              :max="99999"
              :disabled="loading"
              controls-position="right"
            />
          </div>

          <!-- 1日の最大当選数 -->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="1日の最大当選数(0 = 制限なし)"></FormItemLabel>
              <HelpTooltip>{{ '1日の最大当選数を設定できます。(最終日を除く)' }}</HelpTooltip>
            </div>
            <el-input-number
              v-model="formData.dailyWinnerCount"
              :min="0"
              :max="99999"
              :disabled="loading"
              controls-position="right"
              :step="1"
              @change="onChangeDailyWinnerCount"
            />
          </div>

          <!-- 当選率変動 -->
          <div class="input-container">
            <div class="title">
              <FormItemLabel label="当選率変動" required></FormItemLabel>
            </div>
            <div class="input">
              <el-radio border label="none" v-model="formData.winningRateChangeType" :disabled="loading">
                {{ '変動なし' }}
              </el-radio>
              <el-tooltip
                placement="top"
                :content="'当選数が全てはけるように、残り時間と残り当選数に応じて当選率を調整します。'"
              >
                <el-radio border label="auto" v-model="formData.winningRateChangeType" :disabled="loading">
                  {{ '自動変動' }}
                </el-radio>
              </el-tooltip>
            </div>
          </div>

          <!-- 当選率 -->
          <div class="input-container" v-if="formData.winningRateChangeType === 'none'">
            <div class="title">
              <FormItemLabel label="当選率(%)" required></FormItemLabel>
              <HelpTooltip>{{ '応募の当選確率を入力してください。' }}</HelpTooltip>
            </div>
            <el-input-number
              v-model="formData.winningRate"
              :min="0"
              :max="100"
              :step="1"
              :disabled="loading"
              controls-position="right"
              :class="{ 'is-error': validationErrors.winningRate }"
              @change="onChangeWinningRate"
            />
          </div>
        </Panel>
      </div>

      <!-- 応募グループ編集画面では抽選ルールは編集しないため表示しない -->
    </div>

    <div slot="footer" :class="['dialog-footer', { 'dialog-footer-edit': isEditMode }]">
      <el-button v-if="isEditMode" type="default" @click="handleDelete" :disabled="isDeleteDisabled">
        <i class="mdi mdi-delete-forever" />
        {{ '削除' }}
      </el-button>
      <el-button type="primary" @click="handleSave" :disabled="loading || !isFormValid">
        <i class="mdi mdi-content-save" />
        {{ isEditMode ? '保存' : '新規作成' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import axios from 'axios'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, reactive, watch } from 'vue'

import HelpTooltip from '@/client/components/_atoms/HelpTooltip.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import FormItemLabel from '@/client/components/_molecules/FormItemLabel.vue'
import Tags from '@/client/components/_molecules/Tags.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import { TrackingService } from '@/client/services'
import { validateKeywords } from '@/client/utils/validation/keyword'

import { useInstagramInstantwinPrizeStore } from '../hooks/instagramInstantwinPrizeStore'

const { proxy } = getCurrentInstance() as any

type SaveMode = 'create' | 'edit'

interface Emits {
  (e: 'close'): void
  (e: 'saved', mode: SaveMode): void
}

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  prizeId: {
    type: Number,
    default: null
  },
  campaignId: {
    type: Number,
    required: true
  },
  prizeCount: {
    type: Number,
    required: true
  },
  campaignStatus: {
    type: Number,
    default: null
  }
})
const emit = defineEmits<Emits>()

// Store
const prizeStore = useInstagramInstantwinPrizeStore()
const { prize, loading } = storeToRefs(prizeStore)

// State
const formData = reactive({
  name: '',
  entryKeyword: [] as string[],
  maxWinnerCount: 0,
  dailyWinnerChangeTime: '00:00',
  dailyWinnerCount: 0,
  winningRateChangeType: 'none' as 'none' | 'auto',
  winningRate: 0
})

const validationErrors = reactive({
  name: '',
  winningRate: ''
})

// Computed
const isEditMode = computed(() => props.prizeId !== null && props.prizeId !== undefined)
const normalizedEntryKeywords = computed(() => formData.entryKeyword.map(keyword => keyword.trim()).filter(Boolean))
const isCampaignFinished = computed(() =>
  [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
    props.campaignStatus as number
  )
)
const isDeleteDisabled = computed(() => loading.value || props.prizeCount <= 1 || isCampaignFinished.value)

const isFormValid = computed(() => {
  // 必須フィールドチェック
  if (!formData.name || formData.name.trim() === '') {
    return false
  }

  // 数値範囲チェック
  if (formData.maxWinnerCount < 0) {
    return false
  }

  if (formData.winningRateChangeType === 'none') {
    if (formData.winningRate < 0 || formData.winningRate > 100) {
      return false
    }
  }

  if (formData.dailyWinnerCount < 0) {
    return false
  }

  // 1日の最大当選数は当選数を超えられない
  if (formData.dailyWinnerCount > 0 && formData.dailyWinnerCount > formData.maxWinnerCount) {
    return false
  }

  return true
})

// Methods
const validateForm = () => {
  let isValid = true

  // 応募グループ名の検証
  if (!formData.name || formData.name.trim() === '') {
    isValid = false
  }

  // 当選数の検証
  if (formData.maxWinnerCount < 0) {
    isValid = false
  }

  // 当選率の検証（変動なしの場合のみ）
  if (formData.winningRateChangeType === 'none') {
    if (formData.winningRate < 0 || formData.winningRate > 100) {
      isValid = false
    }
  }

  // 1日の最大当選数の検証
  if (formData.dailyWinnerCount < 0) {
    isValid = false
  }

  if (formData.dailyWinnerCount > 0 && formData.dailyWinnerCount > formData.maxWinnerCount) {
    isValid = false
  }

  return isValid
}

const resetFormData = () => {
  formData.name = ''
  formData.entryKeyword = []
  formData.maxWinnerCount = 0
  formData.dailyWinnerChangeTime = '00:00'
  formData.dailyWinnerCount = 0
  formData.winningRateChangeType = 'none'
  formData.winningRate = 0
}

const applyPrizeData = (prizeData: any) => {
  formData.name = prizeData.name
  try {
    formData.entryKeyword = prizeData.entryKeyword ? JSON.parse(prizeData.entryKeyword) : []
  } catch {
    formData.entryKeyword = []
  }
  formData.maxWinnerCount = prizeData.maxWinnerCount
  formData.dailyWinnerChangeTime = prizeData.dailyWinnerChangeTime
    ? prizeData.dailyWinnerChangeTime.substring(0, 5)
    : '00:00'
  formData.dailyWinnerCount = prizeData.dailyWinnerCount
  formData.winningRateChangeType = prizeData.winningRateChangeType as 'none' | 'auto'
  formData.winningRate = prizeData.winningRate
}

const notifyKeywordValidationError = (errorMessage?: string) => {
  proxy.$notify({
    title: errorMessage || 'キーワードに利用できない文字列が含まれています。',
    customClass: 'danger',
    duration: 5000
  })
}

const validateEntryKeywords = (keywords: string[], origin: string[] = []) => {
  return validateKeywords(keywords, origin, { mode: 'whitespace_and_length_only' })
}

const onChangeEntryKeywords = (keywords: string[]) => {
  const origin = [...formData.entryKeyword]
  const result = validateEntryKeywords(keywords, origin)

  if (!result.isValid) {
    notifyKeywordValidationError(result.errorMessage)
    return
  }

  formData.entryKeyword = keywords
}

const fetchPrizeData = async () => {
  if (!props.prizeId) {
    return
  }

  try {
    await prizeStore.fetchPrize(props.prizeId)
  } catch {
    proxy.$notify({
      title: 'データの読み込みに失敗しました。',
      customClass: 'danger',
      duration: 5000
    })
  }
}

const getErrorType = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.type || null
  }

  const errorData = error as any
  return errorData?.response?.data?.error?.type || errorData?.error?.type || null
}

const getFailedMessage = (errorType: string | null) => {
  if (!errorType) {
    return isEditMode.value
      ? 'データの保存に失敗しました。しばらくしてから再度お試しください。'
      : '応募グループの新規作成に失敗しました。しばらくしてから再度お試しください。'
  }

  switch (errorType) {
    case 'PARAMETER_INVALID':
      return '入力内容に誤りがあります。'
    case 'NOT_EXISTS':
      return isEditMode.value ? '応募グループが見つかりませんでした。' : 'キャンペーンが見つかりませんでした。'
    default:
      return isEditMode.value
        ? 'データの保存に失敗しました。しばらくしてから再度お試しください。'
        : '応募グループの新規作成に失敗しました。しばらくしてから再度お試しください。'
  }
}

const getDeleteFailedMessage = (errorType: string | null) => {
  switch (errorType) {
    case 'NOT_EXISTS':
      return '対象の応募グループはすでに削除されています。'
    case 'PARAMETER_INVALID':
      return '応募グループを削除できませんでした。'
    default:
      return '応募グループの削除に失敗しました。しばらくしてから再度お試しください。'
  }
}

const handleSave = async () => {
  // バリデーションエラーをクリア
  validationErrors.name = ''

  // バリデーション
  if (!validateForm()) {
    // バリデーションエラーを表示
    if (!formData.name || formData.name.trim() === '') {
      validationErrors.name = '応募グループ名を入力してください'
    }
    return
  }

  const entryKeywordValidationResult = validateEntryKeywords(formData.entryKeyword)
  if (!entryKeywordValidationResult.isValid) {
    notifyKeywordValidationError(entryKeywordValidationResult.errorMessage)
    return
  }

  try {
    loading.value = true

    // API送信データの準備
    const saveData = {
      name: formData.name,
      entryKeyword: normalizedEntryKeywords.value,
      maxWinnerCount: formData.maxWinnerCount,
      // HH:mm形式をHH:mm:ss形式に変換
      dailyWinnerChangeTime: formData.dailyWinnerChangeTime ? `${formData.dailyWinnerChangeTime}:00` : '00:00:00',
      dailyWinnerCount: formData.dailyWinnerCount,
      winningRateChangeType: formData.winningRateChangeType,
      winningRate: formData.winningRate
    }

    const mode: SaveMode = isEditMode.value ? 'edit' : 'create'

    if (mode === 'edit') {
      await axios.put(`/api/instagram_instantwin_prizes/${props.prizeId}`, saveData)
      TrackingService.sendEvent('save:応募グループ編集（IGIW）')
      proxy.$notify({
        title: '応募グループを保存しました。',
        customClass: 'success',
        duration: 5000
      })
    } else {
      await prizeStore.createPrize({
        campaignId: props.campaignId,
        ...saveData
      })
      TrackingService.sendEvent('save:応募グループ新規作成（IGIW）')
      proxy.$notify({
        title: '応募グループを作成しました。',
        customClass: 'success',
        duration: 5000
      })
    }

    // 保存成功イベントを発火
    emit('saved', mode)
    emit('close')
  } catch (error) {
    proxy.$notify({
      title: getFailedMessage(getErrorType(error)),
      customClass: 'danger',
      duration: 5000
    })

    // トラッキング
    TrackingService.sendEvent(
      isEditMode.value ? 'error:応募グループ編集（IGIW）|保存失敗' : 'error:応募グループ新規作成（IGIW）|作成失敗'
    )
  } finally {
    loading.value = false
  }
}

const handleDelete = async () => {
  if (!props.prizeId || loading.value) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|応募グループ編集|削除')

  const confirmDelete = confirm('応募グループを削除してよろしいですか？')
  if (!confirmDelete) {
    return
  }

  try {
    loading.value = true
    await axios.delete(`/api/instagram_instantwin_prizes/${props.prizeId}`)
    proxy.$notify({
      title: '応募グループを削除しました。',
      customClass: 'success',
      duration: 5000
    })
    emit('saved', 'edit')
    emit('close')
  } catch (error) {
    const errorType = getErrorType(error)
    proxy.$notify({
      title: getDeleteFailedMessage(errorType),
      customClass: 'danger',
      duration: 5000
    })
    TrackingService.sendEvent('error:応募グループ編集（IGIW）|削除失敗')
    if (errorType === 'NOT_EXISTS') {
      emit('saved', 'edit')
      emit('close')
    }
  } finally {
    loading.value = false
  }
}

const handleClose = () => {
  emit('close')
}

const onChangeDailyWinnerCount = (dailyWinnerCount: number) => {
  if (!dailyWinnerCount) {
    setTimeout(() => {
      formData.dailyWinnerCount = 0
    }, 100)

    return
  }

  if (dailyWinnerCount > formData.maxWinnerCount) {
    setTimeout(() => {
      formData.dailyWinnerCount = 0
    }, 100)
  }
}

const onChangeWinningRate = (winningRate: number) => {
  if (!winningRate) {
    setTimeout(() => {
      formData.winningRate = 0
    }, 100)
    return
  }

  // 整数の場合は小数点を削除（100.00 → 100, 50.00 → 50）
  if (Number.isInteger(winningRate)) {
    setTimeout(() => {
      formData.winningRate = Math.floor(winningRate)
    }, 100)
  }
}

// Watch
watch(
  () => prize.value,
  prizeData => {
    if (props.visible && isEditMode.value && prizeData) {
      applyPrizeData(prizeData)
    }
  }
)

watch(
  () => props.visible,
  newValue => {
    if (newValue && isEditMode.value) {
      resetFormData()
      fetchPrizeData()
    } else if (newValue && !props.prizeId) {
      resetFormData()
    } else if (!newValue) {
      // ダイアログが閉じられたときにバリデーションエラーをクリア
      resetFormData()
      validationErrors.name = ''
    }
  }
)

watch(
  () => formData.name,
  newValue => {
    // フォームが空欄の場合はエラーを表示、入力がある場合はエラーをクリア
    if (!newValue || newValue.trim() === '') {
      validationErrors.name = '応募グループ名を入力してください'
    } else {
      validationErrors.name = ''
    }
  }
)
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-dialog) {
  .el-dialog__header {
    padding: $spacing-unit * 2 $spacing-unit * 2;
    font-size: 16px;
    color: $text-color-white !important;
    background-color: $background-dark-color;
  }

  .el-dialog__body {
    padding: $spacing-unit * 2;
    padding-bottom: 0;
  }

  .el-dialog__footer {
    padding: 0 $spacing-unit * 2 $spacing-unit * 2;
  }

  .el-dialog__title,
  .el-dialog__headerbtn .el-dialog__close {
    color: $text-color-white;
  }
}

.instagram-instantwin-prize-edit-dialog {
  .caption {
    margin: $spacing-unit * 2 0 $spacing-unit * 2;
    font-size: 16px;
    font-weight: bold;
    color: $text-color;

    &:first-child {
      margin-top: 0;
    }
  }

  .panel {
    margin-bottom: $spacing-unit * 2;
  }

  .input-container {
    width: 100%;

    &:not(:last-child) {
      margin-bottom: $spacing-unit * 2;
    }

    .title {
      display: flex;
      align-items: center;
      margin-bottom: $spacing-unit;
    }

    .input {
      display: block;
    }

    .el-input-number {
      width: $spacing-unit * 18;
    }

    .el-switch {
      margin-right: $spacing-unit;
    }
  }

  .input-invalid-text {
    margin-top: math.div($spacing-unit, 2);
    font-size: 16px;
    color: $danger-color;
  }

  .help-text {
    margin-bottom: $spacing-unit;
  }

  // エラー状態のフォームスタイル
  .is-error {
    :deep(.el-input__inner) {
      border-color: $danger-color !important;
    }
  }

  .switch-with-help {
    display: flex;
    align-items: center;
  }

  .dialog-footer {
    display: flex;
    gap: $spacing-unit;
    justify-content: flex-end;
    margin-top: $spacing-unit * 2;
  }

  .dialog-footer-edit {
    justify-content: space-between;
  }

  :deep(.daily-change-time-picker) {
    .el-input__prefix {
      display: flex;
      align-items: center;
      height: 100%;
    }

    .el-input__prefix .mdi {
      line-height: 1;
      vertical-align: middle;
    }
  }
}
</style>
